import { useCallback, useEffect, useState } from "react"
import Button from "../Button"
import Select from "../Select"
import Dialog from "./Dialog"
import styles from "./UserCourseDialog.module.css"
import api from "../../api"
import { DialogStatus } from "../../enums"
import AlertDialog from "./AlertDialog"
import Checkbox from "../Checkbox"
import MultipleSelect from "../MultipleSelect"

const UserCourseDialog = ({ user, courses, onClose, course_id, edition_id, limitModules, limited_modules }) => {

    const [coursesOptions, setCoursesOptions] = useState(Array.from(
        courses.reduce((map, c) => {
            if (!map.has(c.id)) {
                map.set(c.id, { id: c.id, name: c.name });
            }
            return map;
        }, new Map()).values()
    ))
    const [editions, setEditions] = useState([])
    const [selectedCourse, setSelectedCourse] = useState(null)
    const [selectedEdition, setSelectedEdition] = useState(null)
    const [loadingEditions, setLoadingEditions] = useState(false)
    const [recordings, setRecordings] = useState(true)
    const [createOrder, setCreateOrder] = useState(false)
    const [sendMail, setSendMail] = useState(!(course_id || edition_id))
    const [alert, setAlert] = useState({
        open: false,
        title: "",
        message: "",
        status: DialogStatus.Default,
        actions: []
    })
    const [dialogTimeout, setDialogTimeout] = useState(null)
    const [subscribing, setSubscribing] = useState(false)
    const [modules, setModules] = useState([])
    const [loadingModules, setLoadingModules] = useState(false)
    const [_limitModules, setLimitModules] = useState(limitModules)
    const [selectedModules, setSelectedModules] = useState([])

    useEffect(() => {
        if (limited_modules) {
            setSelectedModules(modules.filter(m => limited_modules.includes(m.id)).map(m => m.id))
        }
    }, [limited_modules, modules])

    useEffect(() => {
        return () => {
            if (dialogTimeout) {
                clearTimeout(dialogTimeout)
            }
        }
    }, [])

    useEffect(() => {
        setCoursesOptions(
            Array.from(
                courses.sort((a, b) => { return a.name.localeCompare(b.name) }).reduce((map, c) => {
                    if (!map.has(c.id)) {
                        map.set(c.id, { id: c.id, name: c.name });
                    }
                    return map;
                }, new Map()).values()
            )
        );
    }, [courses])

    useEffect(() => {
        if (course_id) {
            setSelectedCourse(courses.find(c => c.id === course_id))
        }
    }, [course_id, courses])

    useEffect(() => {
        setSendMail(!(course_id || edition_id))
    }, [course_id, edition_id])

    useEffect(() => {
        if (selectedCourse?.id) {
            getEditions(selectedCourse.id)
        } else {
            setEditions([])
            setSelectedEdition(null)
            setModules([])
        }
    }, [selectedCourse?.id])

    const getEditions = async (course_id) => {
        setLoadingEditions(true)
        try {
            let editions = await api.get(`/admin/courses/${course_id}/editions`)
            setEditions(editions
                .filter(e => {
                    return limitModules ? true : !user.courses.map(c => c.edition?.id).includes(e.id)
                })
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            )
        } catch (e) {
            console.error(e)
        }
        setLoadingEditions(false)
    }

    const getEditionModules = async (editionId) => {
        if (!editionId) {
            setModules([])
            return
        }

        setLoadingModules(true)
        try {
            const edition = await api.get(`/admin/editions/${editionId}`)
            const editionModules = Array.isArray(edition?.modules) ? edition.modules : []
            setModules(editionModules.sort((a, b) => Number(a.position || 0) - Number(b.position || 0)))
        } catch (e) {
            console.error(e)
            setModules([])
        }
        setLoadingModules(false)
    }

    const subscribe = async () => {
        setSubscribing(true)
        try {
            let url = selectedEdition
                ? `/admin/users/${user.id}/courses/${selectedCourse.id}/editions/${selectedEdition.id}${edition_id ? `?updating_edition=${edition_id}&recordings=${recordings}&send_mail=${sendMail}` : `?recordings=${recordings}&create_order=${createOrder}&send_mail=${sendMail}`}`
                : `/admin/users/${user.id}/courses/${selectedCourse.id}?recordings=${recordings}&create_order=${createOrder}&send_mail=${sendMail}`;

            if (selectedModules && selectedModules.length > 0) {
                url += `&modules=${selectedModules.join(',')}`;
            }

            await api.post(url);

            setSelectedCourse(null)
            setSelectedEdition(null)
            setAlert(a => {
                return {
                    ...a,
                    actions: [],
                    status: DialogStatus.Success,
                }
            })
            setDialogTimeout(setTimeout(() => {
                resetAlert()
                onClose()
            }, 2000))
        } catch (e) {
            console.error(e)
        }
        setSubscribing(false)
    }

    const resetAlert = useCallback(() => {
        setAlert({
            open: false,
            title: "",
            message: "",
            status: DialogStatus.Default,
            actions: []
        })
    }, [])

    const openConfirmDialog = (selectedCourse, selectedEdition) => {
        setAlert({
            open: true,
            title: `Confermi l'iscrizione ?`,
            message: !selectedEdition ? `Confermi di voler iscrivere ${user.name} ${user.surname} al corso di ${selectedCourse.name}?` : `Confermi di voler iscrivere ${user.name} ${user.surname} al corso di ${selectedCourse.name}, ${selectedEdition.name}?`,
            status: DialogStatus.Default,
            actions: [
                {
                    label: 'ANNULLA',
                    onClick: () => {
                        setAlert((prev) => ({ ...prev, open: false }))
                        onClose()
                    }
                },
                {
                    label: 'CONFERMA',
                    disabled: subscribing,
                    onClick: async () => {
                        await subscribe()
                    }
                }
            ]
        })
    }

    useEffect(() => {
        if (selectedEdition?.id) {
            getEditionModules(selectedEdition.id)
        } else {
            setModules([])
        }
    }, [selectedEdition?.id])

    useEffect(() => {
        if (edition_id && editions) {
            setSelectedEdition(editions.find(e => e.id === edition_id))
        }
    }, [edition_id, editions])

    const loadingCourseSelection = Boolean(course_id) && !selectedCourse
    const canShowLimitModulesControls = Boolean(selectedEdition) && !loadingCourseSelection && !loadingEditions

    return (
        <>
            <Dialog
                open={true}
                title={"Iscrivi ad un corso"}
                style={{ maxWidth: "560px", overflow: "visible" }}
                overflow="visible"
                onClose={onClose}
                action={
                    <div className={styles.actions}>
                    <Button appearance="subtle" requiresEdit={false} onClick={onClose}>Annulla</Button>
                    <Button onClick={() => {
                        openConfirmDialog(selectedCourse, selectedEdition)
                    }} disabled={!selectedCourse}>
                        {!selectedEdition ? 'Iscrivi al corso' : (edition_id ? 'Salva' : 'Iscrivi all’edizione')}
                    </Button>
                    </div>
                }
            >
                <div className={styles.dialog}>
                    <p className={styles.intro}>Seleziona il corso e l’eventuale edizione per <strong>{user.name} {user.surname}</strong>.</p>
                    <div className={styles.field}>
                    <span>Corso</span>
                    <Select portal appearance="modern"
                        disabled={courses.length === 0 || course_id}
                        loading={Boolean(course_id) && !selectedCourse}
                        placeholder={"Scegli il corso"}
                        selected={selectedCourse ? coursesOptions.find(c => c.id === selectedCourse.id) ?? selectedCourse : null}
                        options={coursesOptions}
                        nullable
                        nullLabel="Scegli il corso"
                        onSelect={(item) => {
                            const nextCourse = item?.id ? courses.find(c => course_id ? c.id === course_id : c.id === item.id) : null
                            setSelectedCourse(nextCourse ?? null)
                            setSelectedEdition(null)
                            setModules([])
                        }} />
                    </div>

                    {selectedCourse &&
                        <div className={styles.field}>
                        <span>Edizione</span>
                        <Select portal appearance="modern"
                            disabled={!selectedCourse || loadingEditions || limitModules}
                            placeholder={"Scegli un'edizione"}
                            selected={selectedEdition ? editions.find(e => e.id === selectedEdition.id) ?? selectedEdition : null}
                            options={editions.sort((a, b) => { return new Date(b.created_at) - new Date(a.created_at) }).map(e => { return { ...e, name: `${e.name} ${e.status === 'ended' ? '(TERMINATA)' : ''}` } })}
                            nullable
                            nullLabel="Scegli un'edizione"
                            loading={loadingEditions}
                            onSelect={(item) => {
                                const nextEdition = item?.id ? editions.find(e => e.id === item.id) : null
                                setSelectedEdition(nextEdition ?? null)
                            }} />
                        <small>Lascia vuoto per iscrivere lo studente al corso senza assegnare un’edizione.</small>
                        </div>
                    }
                    {selectedCourse && !loadingEditions && editions.length === 0 &&
                        <div className={styles.spinnerContainer}>
                            Nessuna edizione disponibile.
                        </div>
                    }
                    {canShowLimitModulesControls &&
                        <>
                            {/* <Checkbox size="1.25rem" radius="5px" label="Registrazioni" labelPosition="end" value={recordings} onChange={(checked) => setRecordings(checked)} /> */}
                            <Checkbox size="1.25rem" radius="5px" label="Limita moduli" labelPosition="end" value={_limitModules} onChange={(checked) => {
                                setLimitModules(checked)
                                if (!checked) {
                                    setSelectedModules([])
                                }
                            }} />
                        </>
                    }
                    {
                        canShowLimitModulesControls && _limitModules &&
                        <div className={styles.field}>
                        <span>Moduli accessibili</span>
                        <MultipleSelect portal appearance="modern"
                        placeholder="Seleziona i moduli"
                        loading={loadingModules}
                        options={modules}
                        selected={modules.filter(m => selectedModules.includes(m.id))}
                        onSelect={(items) => {
                            setSelectedModules(items.map(i => i.id))
                        }} />
                        </div>
                    }
                    {selectedCourse && !edition_id &&
                        <>
                            {/* <Checkbox size="1.25rem" radius="5px" label="Registrazioni" labelPosition="end" value={recordings} onChange={(checked) => setRecordings(checked)} /> */}
                            <Checkbox size="1.25rem" radius="5px" label="Invia mail di notifica" labelPosition="end" value={sendMail} onChange={(checked) => setSendMail(checked)} />
                            <Checkbox size="1.25rem" radius="5px" label="Crea un ordine per questa iscrizione" labelPosition="end" value={createOrder} onChange={(checked) => setCreateOrder(checked)} />
                        </>
                    }
                    {selectedCourse && edition_id &&
                        <Checkbox size="1.25rem" radius="5px" label="Invia mail di notifica" labelPosition="end" value={sendMail} onChange={(checked) => setSendMail(checked)} />
                    }
                </div>
            </Dialog>
            <AlertDialog
                open={alert.open}
                title={alert.title}
                text={alert.message}
                onClose={alert.onClose ?? resetAlert}
                actions={alert.actions}
                status={alert.status}
            />
        </>

    )
}

export default UserCourseDialog